<template>
  <ProSpaceRightPanel
    :header="header"
    :loading="loading"
    :has-error="hasError"
    @edit="editPanel"
    @adjustmentRequest="adjustmentRequest"
    @delete="deletePanel"
    @show-history="showHistoryPanel"
    @showAgencyTerritory="showAgencyTerritoryModal"
  >
    <template v-if="isMultipleView()" v-slot:center>
      <ProSpaceVLayout :gap="10">
        <PanelBlockSelectedItems :items="[
            { name: $t('addressProgramOrderOutletId'), value: 'outletId', f: (v) => v },
            { name: $t('network'), value: 'networkValue', f: (v) => v },
          ]"
        />
        <ProSpacePanelBlock v-if="isAllRowsSelected" class="prospace-panel-block--empty" :header="{title: $t('itemsSelectedAll')}">
          <template #header>
            <ProSpaceAction
              :text="$t('clearTxt')"
              @click="clearPanelAll"
            />
          </template>
        </ProSpacePanelBlock>
        <ProSpacePanelBlock :header="hideAdjustmentRequest ? '' : headerAction">
          <ProSpaceVLayout :gap="10">
            <ProSpaceAction
              v-if="isUserHasAgencyRole && currentPeriod.status != 'closed'"
              :text="$t('setAgencyTerritoryParams')"
              @click="showAgencyTerritoryModal"
            />
          </ProSpaceVLayout>
        </ProSpacePanelBlock>
      </ProSpaceVLayout>
    </template>
    <template v-else-if="selectedCount === 1" v-slot:center>
      <ProSpaceTabs :tabs="tabs">
        <template v-slot:tab-1>
          <ProSpacePanelBlock :header="headerAddressProgramOrders">
            <template v-slot:header>
              <ProSpaceAction
                v-if="isGroupingGrid && !isGroupingOutletsMode"
                :text="$t('open')"
                @click="$emit('openOutletsList')"
              />
            </template>
            <template v-slot>
              <ProSpacePanelBlockList :itemsPerPage="100" :list="content" />
            </template>
          </ProSpacePanelBlock>
        </template>
        <template v-slot:tab-2>
          <ProSpacePanelBlock :header="openParameters">
            <template v-slot:content>
              <ProSpaceHLayout
                class="address-program__kpi-block"
                justify-content="space-between"
              >
                <span class="address-program__kpi-title">{{ $t("ratePerHour") }}</span>
                <span class="address-program__kpi-value">{{
                    formatDigit(selectedData.ratePerHourPlan)
                }}</span>
              </ProSpaceHLayout>
              <ProSpaceHLayout
                class="address-program__kpi-block"
                justify-content="space-between"
              >
                <span class="address-program__kpi-title">{{ $t("timingPerVisit") }}</span>
                <span class="address-program__kpi-value">{{
                  formatDigit(selectedData.timingPerVisitPlan)
                }}</span>
              </ProSpaceHLayout>
            </template>
          </ProSpacePanelBlock>
          <ProSpacePanelBlock :header="openKpi">
            <template v-slot:content>
              <ProSpaceVLayout :gap="8" class="address-program__kpi-block">
                <span class="address-program__kpi-title">{{ $t('fteMerch') }}</span>
                <AddressProgramKpiStats
                  :plan-prop="addressProgramOrder.fteMerchPlan"
                  :fact-prop="addressProgramOrder.fteMerchFact"
                />
              </ProSpaceVLayout>
              <ProSpaceVLayout :gap="8" class="address-program__kpi-block">
                <span class="address-program__kpi-title">{{ $t('visitSum') }}</span>
                <AddressProgramKpiStats
                  :plan-prop="addressProgramOrder.frequencyPlan"
                  :fact-prop="addressProgramOrder.frequencyFact"
                  :format-number="false"
                />
              </ProSpaceVLayout>
            </template>
          </ProSpacePanelBlock>
          <ProSpacePanelBlock :header="businessKpi">
            <template v-slot:content>
              <ProSpaceVLayout :gap="8" class="address-program__kpi-block">
                <span class="address-program__kpi-title">{{ $t('osa') }}</span>
                <AddressProgramKpiStats
                  :plan-prop="addressProgramOrder.osaPlan"
                  :fact-prop="addressProgramOrder.osaFact"
                  :percent="true"
                />
              </ProSpaceVLayout>
              <ProSpaceVLayout :gap="8" class="address-program__kpi-block">
                <span class="address-program__kpi-title">{{ $t('isaOsa') }}</span>
                <AddressProgramKpiStats
                  :plan-prop="addressProgramOrder.deltaISAOSAPlan"
                  :fact-prop="addressProgramOrder.deltaISAOSAFact"
                  :percent="true"
                  :useRawValues="true"
                  :revertKPI="true"
                />
              </ProSpaceVLayout>
              <ProSpaceVLayout :gap="8" class="address-program__kpi-block">
                <span class="address-program__kpi-title">{{ $t('pss') }}</span>
                <AddressProgramKpiStats
                  :plan-prop="addressProgramOrder.pssPlan"
                  :fact-prop="addressProgramOrder.pssFact"
                  :percent="true"
                />
              </ProSpaceVLayout>
              <ProSpaceVLayout :gap="8" class="address-program__kpi-block">
                <span class="address-program__kpi-title">{{ $t('tacticalTask') }}</span>
                <AddressProgramKpiStats
                  :plan-prop="addressProgramOrder.tacticalTaskPlan"
                  :fact-prop="addressProgramOrder.tacticalTaskFact"
                />
              </ProSpaceVLayout>
            </template>
          </ProSpacePanelBlock>
          <ProSpacePanelBlock :header="budgetHeader">
            <template v-slot:content>
              <ProSpaceVLayout
                v-for="(kpi, index) in budgetKpi"
                :key="`${kpi.title}-${index}`"
                :gap="8"
                class="address-program__kpi-block">
                <span v-if="kpi.bold" style="font-weight: 500;">{{ kpi.title }}</span>
                <span v-else >{{ kpi.title }}</span>
                <AddressProgramKpiStats
                  :plan-prop="kpi.plan"
                  :fact-prop="kpi.fact"
                  :formatNumber="kpi.formatNumber"
                  :separatorType="kpi.separatorType"
                />
              </ProSpaceVLayout>
            </template>
          </ProSpacePanelBlock>
        </template>
      </ProSpaceTabs>
    </template>
  </ProSpaceRightPanel>
</template>

<script>
import {
  ProSpaceRightPanel,
  ProSpaceTabs,
  ProSpacePanelBlock,
  ProSpacePanelBlockList,
  ProSpaceAction,
  ProSpaceHLayout,
  ProSpaceVLayout,
  ProSpaceSimpleGrid,
  ProSpaceModal,
  ProSpaceGeoLayout
} from "@prospace/prospace-components-library";
import { AddressProgramOrderService } from "../../../services/AddressProgramOrderService";
import { mapGetters } from "vuex";
import AddressProgramKpiStats from "./AddressProgramKpiStats.vue";
import Column from "primevue/column";
import formatDigit from "@prospace/prospace-components-library/src/lib-assets/mixins/digitFormatter";

import {
  useMultiSelectRightPanel,
} from "@composes/multiselect"
import PanelBlockSelectedItems from "@multiselect/PanelBlockSelectedItems"
export default {
  name: "AddressProgramOrderPanel",
  // mixins: [accessMixin],
  components: {
    ProSpaceSimpleGrid,
    AddressProgramKpiStats,
    ProSpaceRightPanel,
    ProSpaceTabs,
    ProSpacePanelBlock,
    ProSpacePanelBlockList,
    ProSpaceAction,
    ProSpaceHLayout,
    ProSpaceVLayout,
    Column,
    ProSpaceModal,
    ProSpaceGeoLayout,
    PanelBlockSelectedItems
  },
  props: {
    options: {
      type: Array,
      default: () => {
        return [];
      },
    },
    isGroupingGrid: {
      type: Boolean,
      required: true,
    },
    hideRequest: {
      type: Boolean,
      required: true,
    },
    checkAccess: {
      type: Function,
    },
    dotsWrapper: {
      type: Function,
    },
    isGroupingOutletsMode: {
      type: Boolean,
      default: false
    },
    isUserHasAgencyRole: {
      type: Boolean,
      default: false
    },
    isUserHasAgency: {
      type: Boolean,
      default: false
    },
    isAllRowsSelected: {
      type: Boolean,
      default: false
    },
    currentPeriod:{
      type: Object,
      required: true
    }
  },
  setup() {
    const { selectedData, selectedClude, selectedCount, isMultipleView } = useMultiSelectRightPanel()
    return {
      selectedData, selectedClude, selectedCount, isMultipleView
    }
  },
  data() {
    return {
      addressProgramOrder: null,
      loading: false,
      hasError: false,
      addressProgramOrderService: new AddressProgramOrderService(),
      modelModalClude: false,
    };
  },
  computed: {
    ...mapGetters({
      userinfo: "userinfoStore/getUserinfo",
    }),
    hideAdjustmentRequest() {
      return this.hideRequest
      || (Array.isArray(this.addressProgramOrder) && this.addressProgramOrder.some(x => x.orderStatus === "completed"))
      || (this.addressProgramOrder && this.addressProgramOrder.orderStatus === "completed");
    },
    headerAddressProgramOrders() {
      return {
        title: this.$t("parameters"),
        isExpand: false,
      };
    },
    openParameters() {
      return {
        title: this.$t("parameters"),
      };
    },
    openKpi() {
      return {
        title: this.$t("openKpi"),
      };
    },
    businessKpi() {
      return {
        title: this.$t("businessKpi"),
      };
    },
    budgetHeader() {
      return {
        title: this.$t("budget"),
      };
    },
    headerBottom() {
      return {
        title: this.$t("metaInfTxt"),
        isExpand: true,
      };
    },
    tabs() {
      return [
        { name: this.$t("parameters"), isActive: true },
        { name: this.$t("kpi") },
      ];
    },
    header() {
      if (this.isGroupingGrid && !this.isGroupingOutletsMode) {
        return {
          title: this.$t("outletCount"),
          counter: this.selectedData?.outletCount,
          showCounter: this.selectedData?.outletCount,
        };
      }
      if (this.addressProgramOrder && this.selectedCount === 1) {
        return {
          title: this.addressProgramOrder.networkValue,
          id: this.addressProgramOrder.outletId,
          status: this.getStatus(this.addressProgramOrder.status),
          options: this.dotsWrapper ? this.dotsWrapper(this.addressProgramOrder) : this.options,
        };
      } else {
        return {
          title: this.$t("itemsSelectAll"),
          showCounter: true,
          counter: this.getCounter,
        };
      }
    },
    headerAction() {
      return {
        title: this.$t("actions"),
      };
    },
    content() {
      let content = [];
      if (this.addressProgramOrder) {
        const {
          city, area, clusterValue, rsmValue, tdmValue, kaeValue,
          outletTypeValue, externalFormatValue, channelValue, narkaValue, sipChannelValue, externalCode,
          territoryCluster, tariffZone, skyline, agencyValue, coverage, networkValue, kasValue, routeCodeValue,
          merchandiserValue
        } = this.addressProgramOrder
        if (this.isGroupingGrid && !this.isGroupingOutletsMode) {
          content.push(
            [{ title: this.$t("rsm"), text: rsmValue }],
            [{ title: this.$t("tdm"), text: tdmValue }],
            [{ title: this.$t("kae"), text: kaeValue }]
          );

          if (this.checkAccess && this.checkAccess("agencyValue-rightPanel"))
            content.push([{ title: this.$t("agency"), text: agencyValue }]);

          content.push(
            [{ title: this.$t("network"), text: networkValue }],
            [{ title: this.$t("externalFormat"), text: externalFormatValue }],
            [{ title: this.$t("outletType"), text: outletTypeValue }],
            [{ title: this.$t("channel"), text: channelValue }],
            [{ title: this.$t("cluster"), text: clusterValue }],
            [{ title: this.$t("narka"), text: narkaValue }],
            [{ title: this.$t("sipChannel"), text: sipChannelValue }]
          );
          content = content.filter((item) => item[0].text);
        } else {
          content.push(
            [{ title: this.$t("city"), text: city }],
            [{ title: this.$t("area"), text: area }],
            [{ title: this.$t("cluster"), text: clusterValue }],
            [{ title: this.$t("rsm"), text: rsmValue }],
            [{ title: this.$t("tdm"), text: tdmValue }],
            [{ title: this.$t("kae"), text: kaeValue }],
            [{ title: this.$t("outletType"), text: outletTypeValue }],
            [{ title: this.$t("externalFormat"), text: externalFormatValue }],
            [{ title: this.$t("channel"), text: channelValue }],
            [{ title: this.$t("narka"), text: narkaValue }],
            [{ title: this.$t("sipChannel"), text: sipChannelValue }],
            [{ title: this.$t("externalCode"), text: externalCode }],
            [{ title: this.$t("territoryCluster"), text: territoryCluster }],
            [{ title: this.$t("tariffZone"), text: tariffZone }],
            [{ title: this.$t("skyline"), text: skyline }]
          );

          if (this.checkAccess && this.checkAccess("agencyValue-rightPanel"))
            content.push([{ title: this.$t("agency"), text: agencyValue }]);

          content.push([
            {
              title: this.$t("coverage"),
              text: coverage ? this.$t("yes") : this.$t("no"),
            },
          ]);

          if (this.isUserHasAgency) {
            content.push([{ title: this.$t("kAS"), text: kasValue }]);
            content.push([{ title: this.$t("routeCode"), text: routeCodeValue }]);
            content.push([{ title: this.$t("merchandiser"), text: merchandiserValue }]);
          }
        }
      }
      return content;
    },
    contentBottom() {
      if (this.addressProgramOrder) {
        return [
          [
            {
              title: this.$t("createdByTxt"),
              text: this.addressProgramOrder.createdBy,
            },
          ],
          [
            {
              title: this.$t("createdAtTxt"),
              text: new Date(this.addressProgramOrder.createdAt),
              dateFormat: this.userinfo.dateFormat,
            },
            {
              title: this.$t("lastUpdatedTxt"),
              text: new Date(this.addressProgramOrder.lastUpdated),
              dateFormat: this.userinfo.dateFormat,
            },
          ],
        ];
      }
      return [];
    },
    budgetKpi() {
      return [
        {
          title: this.$t('totalSum'),
          plan: this.addressProgramOrder.totalSumPlan,
          fact: this.addressProgramOrder.totalSumFact,
          formatNumber: true,
          separatorType: ".",
          bold: true
        },
        {
          title: this.$t('visitSum'),
          plan: this.addressProgramOrder.visitSumPlan,
          fact: this.addressProgramOrder.visitSumFact,
          formatNumber: true,
          separatorType: "."
        },
        {
          title: this.$t('oSASum'),
          plan: this.addressProgramOrder.osaSumPlan,
          fact: this.addressProgramOrder.osaSumFact,
          formatNumber: true,
          separatorType: "."
        },
        {
          title: this.$t('isaOsaSum'),
          plan: this.addressProgramOrder.deltaISAOSASumPlan,
          fact: this.addressProgramOrder.deltaISAOSASumFact,
          formatNumber: true,
          separatorType: "."
        },
        {
          title: this.$t('pSSSum'),
          plan: this.addressProgramOrder.pssSumPlan,
          fact: this.addressProgramOrder.pssSumFact,
          formatNumber: true,
          separatorType: "."
        },
        {
          title: this.$t('tacticalTaskSum'),
          plan: this.addressProgramOrder.tacticalTaskSumPlan,
          fact: this.addressProgramOrder.tacticalTaskSumFact,
          formatNumber: true,
          separatorType: "."
        }
      ]
    },
    getCounter() {
      return this.selectedCount || 0;
    }
  },
  watch: {
    selectedData: {
      handler: function(val) {
        this.update();
      },
      immediate: true
    },
    selectedCount() {
      this.update()
    },
  },
  methods: {
    formatDigit,
    update() {
      this.hasError = false;
      try {
        this.getAddressProgramOrder();
      } catch (e) {
        this.hasError = true;
      }
    },
    getAddressProgramOrder() {
      this.addressProgramOrder = this.selectedData;
    },
    getStatus(status) {
      if (!status) return;
      let type = "default";
      let text = "";
      switch (status) {
        case "plan":
          type = "info";
          text = this.$t("planStatus");
          break;
        case "draft":
          type = "draft";
          text = this.$t("draftStatus");
          break;
        case "planfact":
          type = "teal";
          text = this.$t("planFactStatus");
          break;
      }
      return {
        text,
        type,
      };
    },
    clearPanel() {
      if (!this.isAllRowsSelected && (this.count === this.clude.items?.length)) {
        this.$emit("clear");
      } else if (this.isAllRowsSelected && (this.count > this.clude.items?.length) && this.clude.isAll) {
        this.$emit("clear:excluded");
      } else {
        this.$emit("clear");
      }
    },
    clearPanelAll() {
      this.$emit("clear");
    },
    editPanel() {
      this.$emit("edit", this.addressProgramOrder);
    },
    deletePanel() {
      this.$emit("delete", this.addressProgramOrder);
    },
    showHistoryPanel() {
      this.$emit("history", this.addressProgramOrder);
    },
    adjustmentRequest() {
      this.$emit("adjustment", this.addressProgramOrder);
    },
    showAgencyTerritoryModal() {
      this.$emit("showAgencyTerritoryModal", this.addressProgramOrder);
    }
  },
};
</script>

<style lang="scss">
.address-program {
  &__kpi {
    &-block {
      margin-top: 5px;
      background-color: var(--prospace-ui-input);
      padding: 8px;
      border-radius: 5px;
      font-size: 11px;
    }
    &-value {
      font-size: 12px;
      font-weight: 500;
      & > span {
        color: var(--prospace-text-gray);
      }
    }
    &-title {
      font-size: 11px;
      color: var(--prospace-text-gray);
    }
  }
  &__order-panel {
    .prospace-list-items .prospace-list-items-table {
      tr > td {
        vertical-align: middle;
        padding: 9px 20px 9px 0 !important;
      }
    }
    .prospace-tabs__header__tabs {
      padding: 0 10px;
    }
    .prospace-panel-header {
      &__title {
        .prospace-tag {
          text-transform: uppercase;
        }
      }
    }
    .prospace-panel-block {
      &__header {
        margin-bottom: 0;
      }
      &--empty {
        .prospace-panel-block-header {
          margin-bottom: 0;
        }
      }
    }
    & > .prospace-geo-layout {
      padding-bottom: 0;
    }
  }
  &__modal-exclude {
    &.prospace-modal.p-dialog.p-component {
      margin-top: 80px;
      max-width: 685px
    }
    .p-datatable .p-datatable-tbody > tr > td:first-child {
      padding-left: 0;
    }
    .prospace-list-items-table .p-datatable-tbody > tr:not(.p-datatable-row-expansion) > td {
      vertical-align: middle;
      &:last-child {
        padding: 5px 0;
      }
    }
  }
}
.kpi-stats {
  height: 32px;
}
</style>
